/**
 * Relatório "Atendentes" (spec 015, US2): por pessoa, pausa (total e por
 * motivo), tempo com conversa, conversas atendidas/encerradas, tickets
 * trabalhados/fechados e horas apontadas.
 *
 * Tela e CSV saem da MESMA lista de colunas (`columns`) e do mesmo
 * formatador — "mesmas colunas e números da tela" (US2-4) é garantido por
 * construção, não por dois códigos que precisam lembrar um do outro. Tempo em
 * horas com duas casas e vírgula (1,50 = 1h30), igual nos dois.
 *
 * Com o filtro de cliente (PoolTrack, 02/10) a API manda `pausesIncluded:
 * false` — pausa não é de cliente nenhum — e as colunas de pausa SAEM da
 * lista, da tela e do CSV juntos, em vez de "0,00" que pareceria medida.
 */
import type { AttendantsReport, AttendantsReportRow } from '@erp/shared';
import { Badge } from '@/components/ui/misc';
import { useTerms } from '@/shared/tenant-profile';
import { TD_NUM, TH, TH_NUM } from './report-format';
import type { CsvTable } from './report-views';

/** minutos → horas "1,50" (duas casas, vírgula) — tela e CSV */
export function hoursCell(minutes: number): string {
  return (minutes / 60).toFixed(2).replace('.', ',');
}

type Cell = string | number;

interface Column {
  header: string;
  numeric: boolean;
  value: (r: AttendantsReportRow) => Cell;
  /** total da coluna; null = não soma (texto) */
  total: ((rows: AttendantsReportRow[]) => Cell) | null;
}

const sum = (rows: AttendantsReportRow[], pick: (r: AttendantsReportRow) => number) =>
  rows.reduce((s, r) => s + pick(r), 0);

function columns(data: AttendantsReport): Column[] {
  const hours = (header: string, pick: (r: AttendantsReportRow) => number): Column => ({
    header,
    numeric: true,
    value: (r) => hoursCell(pick(r)),
    total: (rows) => hoursCell(sum(rows, pick)),
  });
  const count = (header: string, pick: (r: AttendantsReportRow) => number): Column => ({
    header,
    numeric: true,
    value: pick,
    total: (rows) => sum(rows, pick),
  });
  // API anterior ao filtro de cliente não manda o campo: pausas incluídas
  const pauseColumns: Column[] =
    data.pausesIncluded === false
      ? []
      : [
          // "tempo online" (PoolTrack, 08/10): o mesmo log de status, por isso no
          // mesmo grupo — e sai junto com o filtro de cliente
          hours('Disponível (h)', (r) => r.availableMinutes),
          hours('Pausa (h)', (r) => r.pauseMinutes),
          ...data.reasons.map((reason, i) => hours(`${reason.name} (h)`, (r) => r.pauseMinutesByReason[i] ?? 0)),
          {
            header: 'Pausa em aberto',
            numeric: false,
            value: (r) => (r.pauseOpen ? 'Sim' : 'Não'),
            total: null,
          },
        ];
  return [
    ...pauseColumns,
    hours('Em conversa (h)', (r) => r.chatMinutes),
    count('Conversas atendidas', (r) => r.chatsAttended),
    count('Conversas encerradas', (r) => r.chatsClosed),
    count('Tickets trabalhados', (r) => r.ticketsWorked),
    count('Tickets fechados', (r) => r.ticketsClosed),
    hours('Horas apontadas (h)', (r) => r.appointmentMinutes),
  ];
}

export function AttendantsView({ data }: { data: AttendantsReport }) {
  const terms = useTerms();
  const cols = columns(data);
  return (
    <div className="overflow-x-auto">
      {data.pausesIncluded === false ? (
        <p className="border-b border-divider px-4 py-2 text-[13px] text-muted-foreground">
          Filtrado por {terms.client}: as pausas ficam de fora, porque não se ligam a {terms.clients}.
        </p>
      ) : null}
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-muted text-left text-[13px]">
            <th className={TH}>Atendente</th>
            {cols.map((c) => (
              <th key={c.header} className={c.numeric ? TH_NUM : TH}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map((r) => (
            <tr
              key={r.userId}
              className="h-10 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
            >
              <td className="max-w-56 truncate px-4 font-medium" title={r.userName}>
                {r.userName}
              </td>
              {cols.map((c) =>
                c.header === 'Pausa em aberto' ? (
                  <td key={c.header} className="px-4 whitespace-nowrap">
                    {/* intervalo ainda aberto: contado até agora (US2, casos de borda) */}
                    {r.pauseOpen ? (
                      <Badge variant="warning" title="Pausa ainda aberta — contada até agora">
                        Sim
                      </Badge>
                    ) : (
                      'Não'
                    )}
                  </td>
                ) : (
                  <td key={c.header} className={TD_NUM}>
                    {c.value(r)}
                  </td>
                )
              )}
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="h-10 border-t bg-muted text-sm font-semibold">
            <td className="px-4">Total</td>
            {cols.map((c) => (
              <td key={c.header} className={c.numeric ? TD_NUM : 'px-4'}>
                {c.total ? c.total(data.rows) : ''}
              </td>
            ))}
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

export function attendantsCsv(data: AttendantsReport): CsvTable {
  const cols = columns(data);
  return {
    headers: ['Atendente', ...cols.map((c) => c.header)],
    rows: [
      ...data.rows.map((r) => [r.userName, ...cols.map((c) => c.value(r))]),
      ['Total', ...cols.map((c) => (c.total ? c.total(data.rows) : ''))],
    ],
  };
}
